import { pipe } from "fp";

import React from "react";

import { Asset } from "~/Asset";
import * as Driver from "~/Driver";
import * as ErrorMessage from "~/ErrorMessage";
import * as Icon from "~/Icon";
import * as Layout from "~/Layout";
import * as Loading from "~/Loading";
import { Order } from "~/Order";
import { Remote } from "~/Remote";
import { Time } from "~/Time";
import * as Typography from "~/Typography";

export function CurrentAsset({ pairing }: Driver.Table.Datum) {
  return !pairing ? (
    <CurrentAsset.PairingMissing />
  ) : Driver.Pairing.isActive(pairing) ? (
    <CurrentAsset.PairingFound pairing={pairing} />
  ) : (
    <CurrentAsset.PairingMissing pairing={pairing} />
  );
}

export namespace CurrentAsset {
  export const column = () => ({
    title: "Current Asset",
    dataIndex: "pairing",
    sorter,
    render,
  });

  const sorter = (a: Driver.Table.Datum, b: Driver.Table.Datum) =>
    Order.optionally(Driver.Pairing.compare)(a.pairing, b.pairing);

  const render = (_: unknown, datum: Driver.Table.Datum) => (
    <CurrentAsset {...datum} />
  );

  export const PairingFound = ({
    pairing,
  }: {
    readonly pairing: Driver.Pairing.Pairing;
  }) => (
    <>
      <Layout.Inline.Component right>
        <Icon.Link faded />
      </Layout.Inline.Component>
      <AssetLink {...pairing} />
      <LastPairing {...pairing}>Paired since </LastPairing>
    </>
  );

  export const LastPairing = ({
    interval,
    children,
  }: Driver.Pairing.Pairing & { readonly children?: Component.Children }) => (
    <Driver.Table.Detail>
      {children}
      <Typography.Component strong>
        {Time.DateTime.toHMM(interval.start)}
      </Typography.Component>
      &nbsp;on&nbsp;
      <Typography.Component strong>
        {Time.DateTime.toDateOnlyMDYY(interval.start)}
      </Typography.Component>
    </Driver.Table.Detail>
  );

  export const PairingMissing = ({
    pairing,
  }: {
    readonly pairing?: Driver.Pairing.Pairing;
  }) =>
    pipe(
      Driver.Pairing.useFilteredPairingsRemote(),
      Remote.when({
        loading: () => <Loading.Component />,
        error: (error) => <Error message={error.message} />,
        success: () => <Unpaired pairing={pairing} />,
      })
    );

  export const Error = ({ message }: { readonly message?: string }) => (
    <>
      <ErrorMessage.Component danger icon>
        Unavailable
      </ErrorMessage.Component>
      {message && <Driver.Table.Detail>{message}</Driver.Table.Detail>}
    </>
  );

  export const Unpaired = ({
    pairing,
  }: {
    readonly pairing?: Driver.Pairing.Pairing;
  }) =>
    !pairing ? (
      <ErrorMessage.Component notice icon>
        Never Paired
      </ErrorMessage.Component>
    ) : (
      <>
        <Layout.Inline.Component right>
          <Icon.Unlink faded />
        </Layout.Inline.Component>
        Unpaired
        <LastPairing {...pairing}>
          Last paired with <AssetLink {...pairing} /> at&nbsp;
        </LastPairing>
      </>
    );

  export const AssetLink = ({
    asset,
    children,
  }: Driver.Pairing.Pairing & { readonly children?: React.ReactNode }) => (
    <Asset.Link asset={asset}>
      {children}
      {Asset.name({ asset })}
    </Asset.Link>
  );
}
